import { Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, matchPath, useLocation } from 'react-router-dom';
import { Box } from 'pixelize-design-library';

import { ThemeToggle } from '@core/Theme/ThemeToggle';
import { AppErrorBoundary } from '@core/components/Error/AppErrorBoundary';
import { NotFoundPage } from '@core/components/Error/NotFoundPage';
import { DeferredFallback } from '@core/components/Loading/DeferredFallback';
import { AppLayout } from '@core/layout/AppLayout';
import { GuestRoute, ProtectedRoute, VerifyEmailRoute } from '@core/auth/guards';
import { useAuthentication } from '@core/auth/useAuthentication';
import { hasVisitedBefore, markReturningVisitor } from '@core/auth/returningVisitor';
import { clearSessionExpired } from '@core/store/Auth/authSlice';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { homePathForRole } from '@core/utils/roleHome';
import {
  loginElement,
  moduleGuestRoutes,
  modulePublicRoutes,
  moduleRoutes,
  STATIC_PUBLIC_PATHS,
  verifyEmailElement,
  type ModuleRoute
} from '@modules/registry';

/** Where a guest's attempted deep link is parked so login can return them to it. */
export const RETURN_TO_KEY = 'lfour:return-to';

/**
 * One boundary PER ROUTE, keyed on the pathname: a single boundary around the whole tree
 * keeps showing the crash after the user navigates away, and the app looks dead.
 */
function RouteBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return <AppErrorBoundary resetKeys={[location.pathname]}>{children}</AppErrorBoundary>;
}

const renderRoutes = (routes: ModuleRoute[]) =>
  routes.map((route) => (
    <Route
      key={route.path}
      path={route.path}
      element={<RouteBoundary>{route.element}</RouteBoundary>}
    />
  ));

function StoreDeepLink() {
  const location = useLocation();
  useEffect(() => {
    window.sessionStorage.setItem(RETURN_TO_KEY, `${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);
  return <Navigate to='/login' replace />;
}

/** Read live, not frozen in state: signing out mid-session must flip the landing page back. */
function GuestLanding() {
  return hasVisitedBefore() ? <>{loginElement}</> : <Navigate to='/home' replace />;
}

/** The public shell has its own toggle in the navbar, which would cover a floating one. */
function GuestThemeToggle() {
  const { pathname } = useLocation();
  const inPublicShell = modulePublicRoutes.some((r) => matchPath(r.path, pathname));
  if (inPublicShell) return null;
  return (
    <Box position='fixed' top='16px' right='16px' zIndex='docked'>
      <ThemeToggle />
    </Box>
  );
}

/**
 * Auth is expressed as PHASES, not route wrappers: each phase returns a DIFFERENT `<Routes>`
 * tree, so a guest cannot render a protected route — it is not in the tree at all, rather
 * than being in the tree behind a check that a re-render could momentarily pass.
 */
export function AppRouter() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, handleLogout } = useAuthentication();
  const expired = useAppSelector((s) => s.auth.sessionExpired);

  useEffect(() => {
    if (!expired) return;
    // Clear FIRST: `handleLogout` dispatches into the same store, and a flag still set
    // when the next rejected query lands re-triggers this in a loop.
    dispatch(clearSessionExpired());
    handleLogout();
  }, [expired, dispatch, handleLogout]);

  useEffect(() => {
    if (isAuthenticated) markReturningVisitor();
  }, [isAuthenticated]);

  // Phase 3 — the guest tree. Only these paths exist.
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<DeferredFallback />}>
        {/* The shell's NavBar carries the toggle, and a guest never renders the shell —
            without this, colour mode is unreachable until after sign-in. */}
        <GuestThemeToggle />
        <Routes>
          {renderRoutes(modulePublicRoutes)}
          {renderRoutes(moduleGuestRoutes)}
          {/* Login for a browser that has signed in before, the marketing site for a new one. */}
          <Route
            path='/'
            element={
              <RouteBoundary>
                <GuestLanding />
              </RouteBoundary>
            }
          />
          <Route
            path='/verify-email'
            element={
              <RouteBoundary>
                <VerifyEmailRoute>{verifyEmailElement}</VerifyEmailRoute>
              </RouteBoundary>
            }
          />
          <Route path='*' element={<StoreDeepLink />} />
        </Routes>
      </Suspense>
    );
  }

  // Phase 4 — the authenticated shell.
  return (
    <Suspense fallback={<DeferredFallback />}>
      <Routes>
        {renderRoutes(modulePublicRoutes.filter((r) => STATIC_PUBLIC_PATHS.includes(r.path)))}
        <Route
          path='/verify-email'
          element={
            <RouteBoundary>
              <VerifyEmailRoute>{verifyEmailElement}</VerifyEmailRoute>
            </RouteBoundary>
          }
        />
        {/* An authenticated user landing on a guest-only path (back button, stale bookmark) is
            sent home instead of falling through to the catch-all 404 below. */}
        <Route element={<GuestRoute />}>{renderRoutes(moduleGuestRoutes)}</Route>
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path='/' element={<Navigate to={homePathForRole(user?.role)} replace />} />
            {renderRoutes(moduleRoutes)}
            {/* Never the crash fallback: `ErrorDisplay` needs an error and a reset handler,
                and on `path="*"` both are undefined. */}
            <Route
              path='*'
              element={
                <RouteBoundary>
                  <NotFoundPage />
                </RouteBoundary>
              }
            />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
